import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { formatAmount } from '../utils/unitConverter';
import { FOOD_GROUP_ORDER } from '../utils/foodGroup';
import { useLanguage } from '../LanguageContext';
import translations from '../translations';
import './ShoppingList.css';

function ShoppingList({ onBack }) {
  const { language } = useLanguage();
  const t = translations[language];

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('shopping_list_items')
      .select('*')
      .order('checked', { ascending: true })
      .order('food_group')
      .order('ingredient_name');
    setItems(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  async function toggleChecked(item) {
    const updated = !item.checked;
    // Optimistic update
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, checked: updated } : i));
    await supabase
      .from('shopping_list_items')
      .update({ checked: updated })
      .eq('id', item.id);
    fetchItems();
  }

  async function clearChecked() {
    setClearing(true);
    const checkedIds = items.filter(i => i.checked).map(i => i.id);
    if (checkedIds.length) {
      await supabase.from('shopping_list_items').delete().in('id', checkedIds);
    }
    await fetchItems();
    setClearing(false);
  }

  async function clearAll() {
    setClearing(true);
    await supabase.from('shopping_list_items').delete().neq('id', 0);
    setItems([]);
    setClearing(false);
  }

  // Group items, keeping canonical order, unchecked first within each group
  const grouped = {};
  FOOD_GROUP_ORDER.forEach(group => {
    const groupItems = items.filter(i => i.food_group === group);
    if (groupItems.length) grouped[group] = groupItems;
  });
  // Catch anything with an unknown group
  items.forEach(i => {
    if (!FOOD_GROUP_ORDER.includes(i.food_group)) {
      if (!grouped[i.food_group]) grouped[i.food_group] = [];
      if (!grouped[i.food_group].find(x => x.id === i.id)) {
        grouped[i.food_group].push(i);
      }
    }
  });

  const uncheckedCount = items.filter(i => !i.checked).length;
  const checkedCount = items.filter(i => i.checked).length;

  function formatItemAmount(item) {
    if (item.amount === null || item.amount === undefined) return '';
    const formatted = formatAmount(item.amount);
    const unit = (!item.unit || item.unit === 'count') ? '' : item.unit;
    return unit ? `${formatted} ${unit}` : formatted;
  }

  return (
    <div className="sl">
      {/* Top bar */}
      <div className="sl__topbar">
        <button className="sl__back" onClick={onBack}>{t.backToRecipes}</button>
        {items.length > 0 && (
          <div className="sl__actions">
            {checkedCount > 0 && (
              <button className="sl__action" onClick={clearChecked} disabled={clearing}>
                {t.clearChecked} ({checkedCount})
              </button>
            )}
            <button className="sl__action sl__action--danger" onClick={clearAll} disabled={clearing}>
              {t.clearAll}
            </button>
          </div>
        )}
      </div>

      {/* Heading */}
      <div className="sl__header">
        <h1 className="sl__title">{t.shoppingList}</h1>
        {!loading && items.length > 0 && (
          <>
            <p className="sl__subtitle">
              {uncheckedCount > 0 ? t.shoppingItemsRemaining(uncheckedCount) : t.shoppingAllDone}
            </p>
            <div className="sl__progress-bar" role="progressbar" aria-valuenow={checkedCount} aria-valuemax={items.length}>
              <div
                className="sl__progress-fill"
                style={{ width: `${Math.round((checkedCount / items.length) * 100)}%` }}
              />
            </div>
          </>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <p className="sl__empty">{t.loading}</p>
      ) : items.length === 0 ? (
        <div className="sl__empty-state">
          <div className="sl__empty-icon">🧺</div>
          <p className="sl__empty-title">{t.shoppingListEmpty}</p>
          <p className="sl__empty-hint">{t.shoppingListHint}</p>
        </div>
      ) : (
        <div className="sl__groups">
          {Object.entries(grouped).map(([group, groupItems]) => (
            <section key={group} className="sl__group">
              <h2 className="sl__group-label">
                {t.foodGroups?.[group] || group}
              </h2>
              <ul className="sl__list">
                {groupItems.map(item => {
                  const amt = formatItemAmount(item);
                  return (
                    <li
                      key={item.id}
                      className={`sl__item${item.checked ? ' sl__item--checked' : ''}`}
                      onClick={() => toggleChecked(item)}
                      role="checkbox"
                      aria-checked={item.checked}
                    >
                      <span className={`sl__check${item.checked ? ' sl__check--done' : ''}`} />
                      <span className="sl__text">
                        <span className="sl__name">
                          {amt && <span className="sl__amount">{amt} </span>}
                          {item.ingredient_name}
                        </span>
                        {item.recipe_title && (
                          <span className="sl__recipe">{item.recipe_title}</span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

export default ShoppingList;
